import React, { useEffect, useRef, useState } from 'react';
import EmbedDimensionsPopup from './embed-dimensions-popup';

export type EmbedProps = {
  html?: string;
  heightMode?: 'auto' | 'fixed';
  fixedHeight?: number; // px
  bordered?: boolean;
  radius?: number; // px
  shadow?: boolean;
  allowScripts?: boolean;
  allowSameOrigin?: boolean;
  allowPopups?: boolean;
  allowScroll?: boolean;
  removeBg?: boolean;
  interactive?: boolean;
  responsiveFit?: boolean;
  offsetX?: number; // px
  offsetY?: number; // px
  zoom?: number; // 1 = 100%
  dataId?: string; // optional dom id marker for external streaming
};

const Embed: React.FC<EmbedProps & { onApplyInsert?: (payload: { ratio: number; zoom: number }) => void }> = ({
  html = '<div style="padding:16px;font:14px system-ui">Paste your embed code in settings</div>',
  heightMode = 'fixed',
  fixedHeight = 360,
  bordered = true,
  radius = 12,
  shadow = false,
  allowScripts = true,
  allowSameOrigin = true,
  allowPopups = true,
  allowScroll = false,
  removeBg = false,
  interactive = true,
  responsiveFit = false,
  offsetX = 0,
  offsetY = 0,
  zoom = 1,
  dataId,
  onApplyInsert,
}) => {
  // Auto-height disabled to prevent runaway growth. Always use fixed height.

  // Measure container dimensions for an on-canvas badge
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [showDims, setShowDims] = useState(false);
  const [appliedRatio, setAppliedRatio] = useState<number | null>(null); // width / height
  const [appliedZoom, setAppliedZoom] = useState<number | null>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof (window as any).ResizeObserver === 'undefined') return;
    const ro = new (window as any).ResizeObserver((entries: any[]) => {
      const r = entries && entries[0] ? entries[0].contentRect : null;
      if (r) setDims({ w: Math.round(r.width || 0), h: Math.round(r.height || 0) });
    });
    ro.observe(el);
    return () => { try { ro.disconnect(); } catch {} };
  }, []);

  const sandboxParts: string[] = [];
  if (allowScripts) sandboxParts.push('allow-scripts');
  if (allowSameOrigin) sandboxParts.push('allow-same-origin');
  if (allowPopups) sandboxParts.push('allow-popups');
  // Always allow forms and modals for common embeds
  sandboxParts.push('allow-forms', 'allow-modals');

  const sandbox = sandboxParts.join(' ');

  // Compose srcDoc to optionally disable internal scrolling
  const noScrollCss = 'html,body{margin:0;padding:0;overflow:hidden!important;-ms-overflow-style:none;scrollbar-width:none}*{scrollbar-width:none}::-webkit-scrollbar{display:none}';
  const allowScrollEff = appliedRatio ? false : allowScroll;
  // Only clear page-level background; do NOT blanket-clear all descendants
  const stripBgCss = 'html,body{background:transparent!important;background-image:none!important}';
  const fitOn = (!!responsiveFit || appliedRatio !== null) && !(Number(offsetX||0) !== 0 || Number(offsetY||0) !== 0);
  const fitCss = fitOn ? '#__fit_root{transform-origin:0 0;display:block;width:100%}.__fit_wrap{position:relative;width:100%}.__fit_scale{transform-origin:0 0;display:block}#__fit_measure{display:inline-block}' : '';
  const offsetCss = '#__offset_root{display:block;will-change:transform;transform:translate3d(var(--off-x,0px), var(--off-y,0px), 0) scale(var(--zoom,1));transform-origin:0 0;transition:transform 60ms linear;width:calc(100% / var(--zoom,1));}';
  const css = `${allowScrollEff ? '' : noScrollCss}${removeBg ? stripBgCss : ''}${fitCss}`;
  const smartStripScript = removeBg ? `
    <script>(function(){
      try{
        var vw = Math.max(document.documentElement.clientWidth, window.innerWidth||0);
        var vh = Math.max(document.documentElement.clientHeight, window.innerHeight||0);
        var MIN_RATIO = 0.6; // cover at least 60% of viewport
        var CAND = [];
        var kids = Array.prototype.slice.call(document.body.children||[]);
        for (var i=0;i<kids.length;i++){
          var el = kids[i];
          var r = el.getBoundingClientRect();
          var ratio = (Math.max(0,r.width)*Math.max(0,r.height))/(vw*vh);
          if (ratio < MIN_RATIO) continue;
          var cs = getComputedStyle(el);
          var hasBg = (cs.backgroundImage && cs.backgroundImage !== 'none') || (cs.backgroundColor && cs.backgroundColor !== 'transparent');
          if (hasBg) CAND.push(el);
        }
        // Also consider full-viewport fixed, behind content
        var all = document.querySelectorAll('*');
        for (var j=0;j<all.length;j++){
          var e = all[j];
          var st = getComputedStyle(e);
          if (st.position === 'fixed' || st.position === 'absolute'){
            var rr = e.getBoundingClientRect();
            var rp = (Math.max(0,rr.width)*Math.max(0,rr.height))/(vw*vh);
            var hasB = (st.backgroundImage && st.backgroundImage !== 'none') || (st.backgroundColor && st.backgroundColor !== 'transparent');
            if (rp >= 0.9 && hasB) CAND.push(e);
          }
        }
        CAND.forEach(function(el){ el.classList.add('__bg_strip_page'); });
      }catch(e){}
    })();</script>
  ` : '';
  const fitScript = fitOn ? `
    <script>(function(){
      function apply(){
        try{
          var wrap = document.getElementById('__fit_wrap');
          var root = document.getElementById('__fit_root');
          var measure = document.getElementById('__fit_measure');
          if(!wrap || !root) return;
          var w = document.documentElement.clientWidth || wrap.clientWidth;
          var sw = (measure && (measure.scrollWidth || measure.offsetWidth)) || root.scrollWidth || root.offsetWidth || 1;
          var scale = Math.min(1, w / Math.max(1, sw));
          root.style.transform = 'scale(' + scale + ')';
          // maintain full width by compensating scaled width
          root.style.width = (100/scale) + '%';
        }catch(e){}
      }
      window.addEventListener('load', apply);
      window.addEventListener('resize', apply);
      new MutationObserver(apply).observe(document.documentElement, {subtree:true, childList:true, attributes:true});
    })();</script>` : '';

  // Local iframe ref for streaming offset updates via postMessage (no reload flicker)
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // Stream offset changes without touching srcDoc
  useStreamOffsets(iframeRef, Number(offsetX||0), Number(offsetY||0));
  const effectiveZoom = fitOn ? 1 : Number((appliedZoom ?? zoom) || 1);
  useStreamZoom(iframeRef, effectiveZoom);

  const composedSrcDoc = `<!doctype html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/>
    <style>${css}${offsetCss}
      .__bg_strip_page{background:transparent!important;background-image:none!important;box-shadow:none!important;border-color:transparent!important}
      :root{ --off-x: 0px; --off-y: 0px; --zoom: 1; }
    </style>
  </head><body style="margin:0;padding:0;${removeBg ? 'background:transparent' : ''}">${fitOn ? `<div id="__fit_wrap" class="__fit_wrap"><div id="__fit_root" class="__fit_scale"><div id="__fit_measure"><div id="__offset_root">${html}</div></div></div></div>` : `<div id="__offset_root">${html}</div>`}${smartStripScript}${fitScript}
    <script>
      (function(){
        try{
          window.addEventListener('message', function(ev){
            var data = ev && ev.data;
            if (!data || data.__EMBED_MSG !== true) return;
            if (data.type === 'SET_OFFSET'){
              var root = document.getElementById('__offset_root');
              if (!root) return;
              // Update CSS variables for smooth translated movement
              root.style.setProperty('--off-x', (Number(data.x)||0) + 'px');
              root.style.setProperty('--off-y', (Number(data.y)||0) + 'px');
            } else if (data.type === 'SET_ZOOM'){
              var rootZ = document.getElementById('__offset_root');
              if (!rootZ) return;
              var z = Number(data.z);
              if (!isFinite(z) || z <= 0) z = 1;
              rootZ.style.setProperty('--zoom', String(z));
            }
          }, false);
        }catch(e){}
      })();
    </script>
  </body></html>`;

  return (
    <div
      ref={containerRef}
      className={shadow ? 'shadow-sm' : ''}
      style={{
        position: 'relative',
        border: bordered ? '1px solid #E5E7EB' : 'none',
        borderRadius: radius,
        overflow: 'hidden',
        background: removeBg ? 'transparent' : '#fff',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 8,
          top: 8,
          zIndex: 2,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '2px 8px',
          fontSize: 11,
          color: '#374151',
          background: 'rgba(255,255,255,0.92)',
          border: '1px solid #E5E7EB',
          borderRadius: 999,
          backdropFilter: 'saturate(180%) blur(8px)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
        }}
      >
        <span style={{ fontWeight: 600 }}>{dims.w}px × {dims.h}px</span>
        <button
          type="button"
          onClick={() => setShowDims(true)}
          title="Open dimensions preview"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            borderRadius: 6,
            border: '1px solid #E5E7EB',
            background: '#fff',
            color: '#059669'
          }}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="14" rx="2"/>
            <path d="M8 21h8"/>
          </svg>
        </button>
      </div>
      <iframe
        title="HTML Embed"
        srcDoc={composedSrcDoc}
        sandbox={sandbox}
        scrolling={allowScrollEff ? 'auto' : 'no'}
        style={{
          display: 'block',
          width: '100%',
          height: Math.max(120, appliedRatio ? Math.round((dims.w || 0) / Math.max(0.0001, appliedRatio)) : fixedHeight),
          overflow: allowScrollEff ? 'auto' : 'hidden',
          background: removeBg ? 'transparent' : '#fff',
          pointerEvents: interactive ? 'auto' : 'none',
        }}
        ref={iframeRef}
        data-embed-id={dataId || ''}
        onLoad={() => {
          const w = iframeRef.current?.contentWindow;
          if (!w) return;
          try {
            w.postMessage({ __EMBED_MSG: true, type: 'SET_OFFSET', x: Number(offsetX||0), y: Number(offsetY||0) }, '*');
            w.postMessage({ __EMBED_MSG: true, type: 'SET_ZOOM', z: Number(effectiveZoom||1) }, '*');
          } catch {}
        }}
      />
      <EmbedDimensionsPopup
        open={showDims}
        width={dims.w}
        height={dims.h}
        html={html}
        initialRatio={Number((appliedRatio ?? ((dims.w && fixedHeight) ? (dims.w / Math.max(1, fixedHeight)) : (16/9))) || (16/9))}
        onClose={() => setShowDims(false)}
        onApply={({ ratio, zoom }) => {
          setAppliedRatio(Number(ratio)||null);
          setAppliedZoom(Number(zoom)||null);
          try { onApplyInsert && onApplyInsert({ ratio, zoom }); } catch {}
          setShowDims(false);
        }}
      />
    </div>
  );
};

// Stream updates whenever offsets change (avoid srcDoc reload)
const useStreamOffsets = (ref: React.MutableRefObject<HTMLIFrameElement | null>, x: number, y: number) => {
  useEffect(() => {
    const w = ref.current?.contentWindow;
    if (!w) return;
    try { w.postMessage({ __EMBED_MSG: true, type: 'SET_OFFSET', x: Number(x||0), y: Number(y||0) }, '*'); } catch {}
  }, [ref, x, y]);
};

const useStreamZoom = (ref: React.MutableRefObject<HTMLIFrameElement | null>, z: number) => {
  useEffect(() => {
    const w = ref.current?.contentWindow;
    if (!w) return;
    try { w.postMessage({ __EMBED_MSG: true, type: 'SET_ZOOM', z: Number(z||1) }, '*'); } catch {}
  }, [ref, z]);
};

export default Embed;
